import React from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import SEO from '../../components/common/SEO';
import theme from '../../theme';

const PolicySection = styled.section`
  padding: 6rem 0 4rem;
  background-color: ${theme.colors.lightGray};
`;

const PolicyContainer = styled.div`
  max-width: 900px;
  margin: 0 auto;
  background-color: ${theme.colors.white};
  padding: 3rem;
  border-radius: ${theme.borderRadius};
  box-shadow: ${theme.boxShadow};
  
  @media (max-width: ${theme.breakpoints.tablet}) {
    padding: 2rem;
  }
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    padding: 1.5rem;
  }
`;

const PageTitle = styled.h1`
  font-size: 2.5rem;
  color: ${theme.colors.secondary};
  margin-bottom: 1rem;
  
  @media (max-width: ${theme.breakpoints.tablet}) {
    font-size: 2rem;
  }
`;

const UpdateDate = styled.p`
  color: ${theme.colors.darkGray};
  font-size: ${theme.fontSizes.small};
  margin-bottom: 2rem;
  padding-bottom: 2rem;
  border-bottom: 2px solid ${theme.colors.lightGray};
`;

const ContentSection = styled.div`
  margin-bottom: 2.5rem;
`;

const SectionTitle = styled.h2`
  font-size: ${theme.fontSizes.xlarge};
  color: ${theme.colors.secondary};
  margin-bottom: 1rem;
  margin-top: 2rem;
`;

const SectionContent = styled.div`
  font-size: ${theme.fontSizes.medium};
  line-height: 1.8;
  color: ${theme.colors.secondary};
  
  p {
    margin-bottom: 1rem;
  }
  
  ul, ol {
    margin: 1rem 0;
    padding-left: 2rem;
  }
  
  li {
    margin-bottom: 0.75rem;
  }
  
  strong {
    font-weight: 700;
    color: ${theme.colors.secondary};
  }
  
  a {
    color: ${theme.colors.primary};
    text-decoration: underline;
    
    &:hover {
      color: ${theme.colors.accent};
    }
  }
`;

const HighlightBox = styled.div`
  background-color: rgba(139, 92, 246, 0.1);
  border-left: 4px solid ${theme.colors.primary};
  padding: 1.5rem;
  margin: 1.5rem 0;
  border-radius: 8px;
`;

const CookieTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin: 1.5rem 0;
  
  th, td {
    padding: 1rem;
    text-align: left;
    border: 1px solid ${theme.colors.lightGray};
  }
  
  th {
    background-color: ${theme.colors.secondary};
    color: ${theme.colors.white};
    font-weight: 600;
  }
  
  tr:nth-child(even) {
    background-color: rgba(0, 0, 0, 0.02);
  }
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    font-size: ${theme.fontSizes.small};
    
    th, td {
      padding: 0.75rem 0.5rem;
    }
  }
`;

const CookiesPage = () => {
  const { t } = useTranslation();
  
  return (
    <>
      <SEO 
        titleKey="cookies.seo.title"
        descriptionKey="cookies.seo.description"
      />
      
      <PolicySection>
        <div className="container">
          <PolicyContainer>
            <PageTitle>{t('cookies.title')}</PageTitle>
            <UpdateDate>{t('cookies.updateDate')}</UpdateDate>
            
            <ContentSection>
              <SectionContent dangerouslySetInnerHTML={{ __html: t('cookies.intro') }} />
            </ContentSection>
            
            <ContentSection>
              <SectionTitle>{t('cookies.whatAreCookies.title')}</SectionTitle>
              <SectionContent dangerouslySetInnerHTML={{ __html: t('cookies.whatAreCookies.content') }} />
            </ContentSection>
            
            <ContentSection>
              <SectionTitle>{t('cookies.howWeUse.title')}</SectionTitle>
              <SectionContent dangerouslySetInnerHTML={{ __html: t('cookies.howWeUse.content') }} />
            </ContentSection>
            
            <ContentSection>
              <SectionTitle>{t('cookies.types.title')}</SectionTitle>
              
              <SectionContent>
                <h3>{t('cookies.types.essential.title')}</h3>
                <p dangerouslySetInnerHTML={{ __html: t('cookies.types.essential.description') }} />
                
                <h3>{t('cookies.types.functional.title')}</h3>
                <p dangerouslySetInnerHTML={{ __html: t('cookies.types.functional.description') }} />
                
                <h3>{t('cookies.types.analytics.title')}</h3>
                <p dangerouslySetInnerHTML={{ __html: t('cookies.types.analytics.description') }} />
                
                <h3>{t('cookies.types.marketing.title')}</h3>
                <p dangerouslySetInnerHTML={{ __html: t('cookies.types.marketing.description') }} />
              </SectionContent>
            </ContentSection>
            
            <ContentSection>
              <SectionTitle>{t('cookies.management.title')}</SectionTitle>
              <SectionContent dangerouslySetInnerHTML={{ __html: t('cookies.management.content') }} />
            </ContentSection>
            
            <ContentSection>
              <SectionTitle>{t('cookies.contact.title')}</SectionTitle>
              <SectionContent dangerouslySetInnerHTML={{ __html: t('cookies.contact.content') }} />
            </ContentSection>
          </PolicyContainer>
        </div>
      </PolicySection>
    </>
  );
};

export default CookiesPage;

